/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the application.
 * Handles user session management, token storage, and automatic session restoration.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import * as authService from '../services/authService';
import { isMfaChallenge, isNewPasswordRequired } from '../services/authService';
import { saveTokens, clearTokens, getTokens } from '../utils/tokenStorage';
import { mapCognitoError } from '../utils/cognitoErrorMapper';
import { recordLastLogin } from '../services/admin-api';
import {
  beginSSOLogin,
  completeSSOLogin,
  consumeReturnTo,
  isFederatedSession,
  redirectToSSOLogout,
  SSOError,
} from '../services/ssoService';
import { isSsoEnabled } from '../config';

/**
 * User attributes interface
 */
export interface UserAttributes {
  [key: string]: string;
}

/**
 * Authentication context type
 */
export interface AuthContextType {
  user: CognitoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  signUp: (email: string, password: string, attributes?: UserAttributes) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  /** Start Microsoft (Entra) sign-in; redirects away from the app. */
  signInWithSSO: (returnTo?: string) => Promise<void>;
  /** Finish Microsoft sign-in; resolves to the path to navigate to. */
  completeSSOSignIn: (code: string, state: string | null) => Promise<string>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  getCurrentUser: () => Promise<CognitoUser | null>;
  clearError: () => void;
  isMfaPending: boolean;
  submitMfaCode: (code: string) => Promise<void>;
  cancelMfa: () => void;
  isNewPasswordPending: boolean;
  newPasswordRequiredAttrs: string[];
  completeNewPassword: (newPassword: string) => Promise<void>;
  cancelNewPassword: () => void;
}

/**
 * Create the authentication context
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}



/**
 * Authentication Provider Component
 * 
 * Manages authentication state and provides auth methods to child components.
 * Automatically restores session on app load.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaPendingUser, setMfaPendingUser] = useState<CognitoUser | null>(null);
  const [newPasswordUser, setNewPasswordUser] = useState<CognitoUser | null>(null);
  const [newPasswordRequiredAttrs, setNewPasswordRequiredAttrs] = useState<string[]>([]);

  const isAuthenticated = user !== null;
  const isMfaPending = mfaPendingUser !== null;
  const isNewPasswordPending = newPasswordUser !== null;

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Store tokens from Cognito session
   */
  const storeTokensFromSession = useCallback((session: CognitoUserSession) => {
    const accessToken = session.getAccessToken().getJwtToken();
    const idToken = session.getIdToken().getJwtToken();
    const refreshToken = session.getRefreshToken().getToken();
    const expiresAt = session.getAccessToken().getExpiration() * 1000; // Convert to milliseconds

    saveTokens({
      accessToken,
      idToken,
      refreshToken,
      expiresAt,
    });
  }, []);

  /**
   * Everything that happens once a session exists, regardless of how it was
   * obtained (password, MFA, new-password challenge, or Microsoft SSO): persist
   * the tokens, publish the user, and record the login.
   */
  const finalizeSession = useCallback(async (session: CognitoUserSession): Promise<void> => {
    storeTokensFromSession(session);

    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);

    // Record last login timestamp (Requirement 11.2)
    if (currentUser) {
      try {
        const timestamp = new Date().toISOString();
        const userId = currentUser.getUsername();
        await recordLastLogin(userId, timestamp);
      } catch (lastLoginError) {
        // Log error but don't block login - last login recording is supplementary
        console.error('Failed to record last login:', lastLoginError);
      }
    }
  }, [storeTokensFromSession]);

  /**
   * Restore session on app load
   */
  useEffect(() => {
    const restoreSession = async () => {
      try {
        setIsLoading(true);
        const currentUser = await authService.getCurrentUser();
        
        if (currentUser) {
          // Get and store session tokens BEFORE setting user state.
          // This ensures tokens are in localStorage before AuthorizationContext
          // tries to extract groups (which happens when isAuthenticated becomes true).
          const session = await authService.getCurrentSession();
          if (session) {
            storeTokensFromSession(session);
          }
          
          setUser(currentUser);
        }
      } catch (err) {
        console.error('Failed to restore session:', err);
        // Clear any stale tokens
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, [storeTokensFromSession]);

  /**
   * Sign up a new user
   */
  const signUp = useCallback(async (
    email: string,
    password: string,
    attributes: UserAttributes = {}
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.signUp(email, password, attributes);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Confirm user signup with verification code
   */
  const confirmSignUp = useCallback(async (email: string, code: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.confirmSignUp(email, code);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Sign in a user
   * If Cognito returns an MFA challenge, stores the CognitoUser in mfaPendingUser
   * and does not complete authentication. Otherwise proceeds with token storage.
   * Requirements: 3.1, 5.3
   */
  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await authService.signIn(email, password);

      if (isMfaChallenge(result)) {
        // MFA challenge: store CognitoUser, do not complete authentication
        setMfaPendingUser(result.cognitoUser);
        return;
      }

      if (isNewPasswordRequired(result)) {
        setNewPasswordUser(result.cognitoUser);
        setNewPasswordRequiredAttrs(result.requiredAttributes);
        return;
      }

      // Session returned directly — proceed with existing token storage and user setup
      await finalizeSession(result);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [finalizeSession]);

  /**
   * Start Microsoft (Entra) sign-in.
   *
   * Redirects out of the app, so nothing after this runs on success.
   */
  const signInWithSSO = useCallback(async (returnTo = '/'): Promise<void> => {
    setError(null);
    try {
      await beginSSOLogin(returnTo);
    } catch (err: any) {
      const errorMessage = 'Could not start Microsoft sign in. Please try again.';
      console.error('Failed to start SSO login:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  /**
   * Finish Microsoft sign-in from the /auth/callback route.
   *
   * @returns the in-app path the user was heading to when they signed in.
   */
  const completeSSOSignIn = useCallback(async (
    code: string,
    state: string | null
  ): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);
      const cognitoUser = await completeSSOLogin(code, state);
      const returnTo = consumeReturnTo();

      // Tokens are already installed in the Cognito storage by completeSSOLogin,
      // so this takes the same path as a password sign-in from here on.
      const session = await new Promise<CognitoUserSession>((resolve, reject) => {
        cognitoUser.getSession((err: Error | null, s: CognitoUserSession | null) => {
          if (err || !s) {
            reject(err ?? new Error('No session after Microsoft sign in'));
            return;
          }
          resolve(s);
        });
      });

      await finalizeSession(session);
      return returnTo;
    } catch (err: any) {
      const errorMessage = err instanceof SSOError ? err.message : mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [finalizeSession]);

  /**
   * Sign out the current user
   */
  const signOut = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Decide this BEFORE the local session is torn down.
      const wasFederated = isSsoEnabled() && isFederatedSession(getTokens()?.idToken);

      await authService.signOut();
      clearTokens();
      setUser(null);

      // Cognito keeps its own hosted-UI session cookie for a federated sign-in.
      // Leaving it behind means the next "Sign in with Microsoft" silently signs
      // the same person straight back in, which reads as a broken sign-out and is
      // a real problem on a shared machine. This navigates away, so it must come
      // last.
      if (wasFederated) {
        redirectToSSOLogout();
      }
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Request password reset code
   */
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.forgotPassword(email);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Confirm password reset with verification code
   */
  const confirmPassword = useCallback(async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.confirmPassword(email, code, newPassword);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Change password for authenticated user
   */
  const changePassword = useCallback(async (
    oldPassword: string,
    newPassword: string
  ): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.changePassword(oldPassword, newPassword);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Resend confirmation code
   */
  const resendConfirmationCode = useCallback(async (email: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.resendConfirmationCode(email);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get current authenticated user
   */
  const getCurrentUser = useCallback(async (): Promise<CognitoUser | null> => {
    try {
      setError(null);
      return await authService.getCurrentUser();
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      return null;
    }
  }, []);

  /**
   * Submit MFA verification code to complete sign-in
   * Requirements: 3.2, 3.3, 3.4
   */
  const submitMfaCode = useCallback(async (code: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      if (!mfaPendingUser) {
        throw new Error('No MFA challenge pending');
      }

      const session = await authService.submitMfaCode(mfaPendingUser, code);
      await finalizeSession(session);
      setMfaPendingUser(null);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [mfaPendingUser, finalizeSession]);

  /**
   * Cancel MFA challenge and return to credentials form
   * Requirements: 3.4
   */
  const cancelMfa = useCallback((): void => {
    setMfaPendingUser(null);
    setError(null);
  }, []);

  /**
   * Complete the Cognito newPasswordRequired challenge with a new permanent password
   */
  const completeNewPassword = useCallback(async (newPassword: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      if (!newPasswordUser) throw new Error('No new-password challenge pending');
      const session = await authService.completeNewPassword(newPasswordUser, newPassword);
      await finalizeSession(session);
      setNewPasswordUser(null);
      setNewPasswordRequiredAttrs([]);
    } catch (err: any) {
      const errorMessage = mapCognitoError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [newPasswordUser, finalizeSession]);

  /**
   * Cancel new-password challenge and return to credentials form
   */
  const cancelNewPassword = useCallback((): void => {
    setNewPasswordUser(null);
    setNewPasswordRequiredAttrs([]);
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    signUp,
    confirmSignUp,
    signIn,
    signInWithSSO,
    completeSSOSignIn,
    signOut,
    forgotPassword,
    confirmPassword,
    changePassword,
    resendConfirmationCode,
    getCurrentUser,
    clearError,
    isMfaPending,
    submitMfaCode,
    cancelMfa,
    isNewPasswordPending,
    newPasswordRequiredAttrs,
    completeNewPassword,
    cancelNewPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access authentication context
 * 
 * @throws Error if used outside of AuthProvider
 * @returns Authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
